'use client';
import React from 'react';
import { Spinner } from '@/components/primitives/spinner';
import { FeedCommentDto } from '@/api/comment';
import Typography from '@/components/primitives/typography';
import CommentItem from '@/screen/feed/view/components/comment-item';
import styles from './styles.module.scss';

interface CommentListProps {
  comments: FeedCommentDto[];
  isCommentsLoading: boolean;
  isLoadingMore: boolean;
  hasMoreComments: boolean;
  editingComment: FeedCommentDto | null;
  isCommentSubmitting: boolean;
  onEdit: (comment: FeedCommentDto) => void;
  onDelete: (commentId: number) => void;
  onCancelEdit: () => void;
  onUpdateComment: () => void;
  onChangeEditingComment: (text: string) => void;
  onLoadMoreComments: () => void;
}

/**
 * Component for displaying a list of comments
 */
const CommentList: React.FC<CommentListProps> = ({
  comments,
  isCommentsLoading,
  isLoadingMore,
  hasMoreComments,
  editingComment,
  isCommentSubmitting,
  onEdit,
  onDelete,
  onCancelEdit,
  onUpdateComment,
  onChangeEditingComment,
  onLoadMoreComments,
}) => {
  if (isCommentsLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="small" />
        <Typography className={styles.loadingText}>댓글을 불러오는 중...</Typography>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <Typography className={styles.emptyText}>아직 댓글이 없습니다</Typography>
    );
  }

  return (
    <div className={styles.commentsContainer}>
      {comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          editingComment={editingComment}
          isCommentSubmitting={isCommentSubmitting}
          onEdit={onEdit}
          onDelete={onDelete}
          onCancelEdit={onCancelEdit}
          onUpdateComment={onUpdateComment}
          onChangeEditingComment={onChangeEditingComment}
        />
      ))}

      {/* Load more comments button or no more comments message */}
      {comments.length > 0 && (
        <>
          {hasMoreComments ? (
            <div
              className={styles.loadMoreButton}
              onClick={() => {
                if(isLoadingMore) return;
                onLoadMoreComments?.call(null);
              }}
            >
              {isLoadingMore ? (
                <div className={styles.loadingMoreContainer}>
                  <Spinner size="small" />
                  <Typography className={styles.loadingMoreText}>댓글을 불러오는 중...</Typography>
                </div>
              ) : (
                <Typography className={styles.loadMoreText} align='center'>댓글 더 보기</Typography>
              )}
            </div>
          ) : (
            <Typography className={styles.noMoreText} align='center'>더이상 댓글이 없습니다</Typography>
          )}
        </>
      )}
    </div>
  );
};

export default CommentList;
